/**
 * @file LinkComponent.js
 * @description This file contains the LinkComponent which renders customizable link elements in two possible styles:
 * 1. Icon style - displays an icon with a title
 * 2. Multimedia composite style - displays an optional image carousel, formatted text content, and a navigation button
 */
import { useContext, useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import {
    Color,
    FontFamily,
    FontSize,
    TextStyle,
} from '@tiptap/extension-text-style';
import { ListItem } from '@tiptap/extension-list';
import { Placeholder } from '@tiptap/extensions';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import {
    Table,
    TableCell,
    TableHeader,
    TableRow,
} from '@tiptap/extension-table';
import ImageCarousel from '../multimedia-component/ImageCarousel';
import styles from './LinkComponent.module.scss';
import Emoji from '../../utils/tiptap/EmojiExtension';
import { fetchClientSSOById } from '../../services/ssoService';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { useAuthContext } from '../../store/Contexts';
import ResizableImage from '../../utils/tiptap/ResizableImageExtension';
import { VideoNode } from '../new-portal-page/VideoNode';
import { QRCodeNode } from '../new-portal-page/QRCodeNode';
import DivParagraph from '../new-portal-page/DivParagraph';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { getText } from '../../i18n';

function isAssetAnchor(anchor) {
    if (!anchor) {
        return false;
    }

    if (anchor.getAttribute('data-link-type') === 'asset') {
        return true;
    }

    const href = anchor.getAttribute('href') || '';
    return /\/files\/asset\//i.test(href);
}

const LinkComponent = ({
    title,
    icon,
    foregroundColor,
    backgroundColor,
    image,
    caption,
    text,
    button,
    handleSetBackgroundColor,
    tenantSSOId,
    onLinkClick,
}) => {
    const [showSSONotFound, setShowSSONotFound] = useState(false);
    const { publicBrand } = useAuthContext();
    const { showModal } = useContext(ModalContext);

    useEffect(() => {
        const getClientSSO = async () => {
            try {
                const data = await fetchClientSSOById(tenantSSOId);
                // Check if tenantSSOId exists in the filtered SSOs with status "Live"
                const shouldShowSSONotFound = data.status !== 'Live';
                setShowSSONotFound(shouldShowSSONotFound);
                if (shouldShowSSONotFound) handleSetBackgroundColor('#FFFFFF');
                else handleSetBackgroundColor(backgroundColor);
            } catch (error) {
                console.error('Could not fetch client SSO by Id: ', error);
                setShowSSONotFound(true);
                handleSetBackgroundColor('#FFFFFF');
            }
        };

        if (tenantSSOId) {
            getClientSSO();
        } else {
            handleSetBackgroundColor(backgroundColor);
        }
    }, [tenantSSOId]);

    const CustomTableCell = TableCell.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                backgroundColor: {
                    default: null,
                    parseHTML: element =>
                        element.getAttribute('data-background-color'),
                    renderHTML: attributes => {
                        return {
                            'data-background-color': attributes.backgroundColor,
                            style: `background-color: ${attributes.backgroundColor}`,
                        };
                    },
                },
            };
        },
    });

    const editor = useEditor({
        extensions: [
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            TextStyle.configure({ types: [ListItem.name] }),
            FontFamily.configure({
                types: ['textStyle'],
            }),
            FontSize,
            StarterKit.configure({
                paragraph: false,
                // StarterKit v3 bundles these; keep the explicitly configured ones.
                link: false,
                underline: false,
                trailingNode: false,
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
                },
            }),
            Placeholder.configure({
                placeholder: getText('msg_8b4539a50a3b'),
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                protocols: ['ftp', 'mailto'],
                HTMLAttributes: {
                    class: '',
                },
            }),
            ResizableImage,
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            CustomTableCell,
            Emoji,
            VideoNode,
            QRCodeNode,
            DivParagraph,
        ],
        content: normalizeHtmlForEditorLoad(text),
        editable: false, // READ-ONLY
    });

    function normalizeHtmlForEditorLoad(html) {
        if (typeof html !== 'string' || !html) return html || '';

        return html.replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, '<div></div>');
    }

    useEffect(() => {
        if (!editor || editor.isDestroyed) {
            return;
        }

        if (editor.getHTML() !== text) {
            const normalizedText = normalizeHtmlForEditorLoad(text);
            editor.commands.setContent(normalizedText, { emitUpdate: false });
        }
    }, [editor, text]);

    if (!editor || editor.isDestroyed) {
        return null;
    }

    if (!editor) {
        return null;
    }

    function handleContentClickCapture(event) {
        const assetAnchor = event.target.closest('a');

        if (!isAssetAnchor(assetAnchor)) {
            return;
        }

        const href = assetAnchor.getAttribute('href');
        if (!href) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        showModal({
            type: ModalTypes.ASSET_VIEW,
            title: assetAnchor.textContent?.trim() || 'Asset Preview',
            text: href,
        });
    }

    const hasMedia = image !== null && image !== undefined;
    const isTextBlank =
        text === undefined ||
        text === '' ||
        text === null ||
        text === '<div><br></div>' ||
        text === '<div></div>';
    const hasButton = button && button !== '';
    const isImageOnly = hasMedia && !icon && isTextBlank && !hasButton;

    if (showSSONotFound) {
        return (
            <NotFoundBox
                backgroundColor="#f0f4ff"
                title={getText('msg_7cc6a6e8c21a')}
                description={getText('msg_8bd86c93ba96')}
                width="100%"
            />
        );
    } else {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isImageOnly ? '0px' : '20px',
                    width: '100%',
                    height: isImageOnly ? '100%' : 'auto',
                    marginTop: isImageOnly ? '0' : 'auto',
                    marginBottom: isImageOnly ? '0' : 'auto',
                    verticalAlign: 'middle',
                    overflow: isImageOnly ? 'hidden' : 'visible',
                }}
            >
                {icon ? (
                    // IF icon IS NOT NULL, RENDER ICON DISPLAY OF CHILD PORTAL CONTAINER
                    <>
                        <div
                            className={`d-flex ${styles.iconContainer}`}
                            style={{ backgroundColor: backgroundColor }}
                        >
                            <div className={styles.iconLinkView}>
                                <i
                                    className={`${styles.iconLinkMedia} bi bi-${icon}`}
                                    style={{ color: foregroundColor }}
                                ></i>
                                <p
                                    className={`fw-semibold ${styles.iconLinkText}`}
                                    style={{
                                        color: foregroundColor,
                                        margin: '0',
                                    }}
                                >
                                    {title}
                                </p>
                            </div>
                        </div>
                    </>
                ) : (
                    // IF icon IS NULL, RENDER MULTIMEDIA COMPOSITE DISPLAY OF CHILD PORTAL CONTAINER
                    <>
                        {image !== null && image !== undefined ? (
                            <ImageCarousel
                                images={[image]}
                                caption={caption}
                                fill={isImageOnly}
                            />
                        ) : null}
                        {!isTextBlank ? (
                            <div
                                style={{
                                    margin: hasMedia ? '0 20px' : '0px',
                                }}
                                onClickCapture={handleContentClickCapture}
                            >
                                <EditorContent
                                    editor={editor}
                                    className={`custom-readonly-editor ${styles.LinkComponentEditorMargins}`}
                                />
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        );
    }
};

export default LinkComponent;
